import { useEffect, useMemo, useState, type JSX } from "react";
import type { Element } from "hast";
import { Maximize2 } from "lucide-react";
import { cn, Dialog } from "@ui";
import { useScrollToBottom } from "../../chat-scroll-context";
import {
  TableSelectionProvider,
  useTableSelection,
  extractRowsFromNode,
  rowIdentity,
  type SelectableRow,
} from "./selection-context";
import { SelectionBar } from "./batch-action-bar";
import { SelectionCheckbox } from "./selection-checkbox";

type ExtraProps = { node?: Element | undefined };

export function Table({
  node,
  children,
  ...props
}: JSX.IntrinsicElements["table"] & ExtraProps) {
  const rows: SelectableRow[] = useMemo(
    () => extractRowsFromNode(node as never),
    [node],
  );
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  // Tables add height on render — jump to the bottom so we land on the last
  // message, not mid-conversation.
  const scrollToBottom = useScrollToBottom();
  useEffect(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  const toggle = (key: string) =>
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleAll = () =>
    setSelectedKeys((prev) =>
      prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.key)),
    );

  const recipientIds = rows
    .filter((r) => selectedKeys.has(r.key))
    .map((r) => r.userId);
  const allChecked = rows.length > 0 && selectedKeys.size === rows.length;
  const indeterminate = selectedKeys.size > 0 && !allChecked;
  const [expanded, setExpanded] = useState(false);

  // Inline view: fixed layout with a forced-wider label column (2nd cell, after
  // the checkbox). Expanded view: auto layout + no truncation so everything
  // breathes in the wider dialog. Props go on the inline table only (rendering
  // them twice would duplicate any id).
  // Truncating, fixed layout in both views; the expanded dialog just gives the
  // same table far more room (75vw), so columns are wide and rarely clip.
  // Props go on the inline table only (rendering twice would duplicate any id).
  const renderTable = (isExpanded: boolean) => (
    <div className="overflow-x-auto !gap-0">
      <table
        className="block sm:table w-[calc(100%_-_4px)] sm:table-fixed border-collapse ring-1 ring-slate-200 rounded-lg overflow-hidden mx-[2px] my-[1px] mb-4 [&_th:nth-child(2)]:w-[200px] [&_th:nth-child(2)]:max-w-none [&_td:nth-child(2)]:w-[200px] [&_td:nth-child(2)]:max-w-none"
        data-streamdown="table"
        {...(isExpanded ? {} : props)}
      >
        {children}
      </table>
    </div>
  );

  return (
    <TableSelectionProvider
      value={{
        rows,
        selectedKeys,
        toggle,
        toggleAll,
        selectableCount: rows.length,
      }}
    >
      <div className="mb-4 !gap-0" data-streamdown="table-wrapper">
        {rows.length > 0 && (
          <SelectionBar
            allChecked={allChecked}
            indeterminate={indeterminate}
            count={selectedKeys.size}
            recipientIds={recipientIds}
            onToggleAll={toggleAll}
          />
        )}
        {renderTable(false)}
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-1 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-default-600 transition-colors"
        >
          <Maximize2 className="h-3 w-3" />
          Agrandir le tableau
        </button>
      </div>

      {expanded && (
        <Dialog
          open={expanded}
          onClose={() => setExpanded(false)}
          title="Détails du tableau"
          className="w-[75vw] max-w-[75vw]"
        >
          {rows.length > 0 && (
            <div className="mb-3">
              <SelectionBar
                allChecked={allChecked}
                indeterminate={indeterminate}
                count={selectedKeys.size}
                recipientIds={recipientIds}
                onToggleAll={toggleAll}
              />
            </div>
          )}
          {renderTable(true)}
        </Dialog>
      )}
    </TableSelectionProvider>
  );
}

export function Thead({
  children,
  ...props
}: JSX.IntrinsicElements["thead"] & ExtraProps) {
  return (
    <thead
      className="hidden sm:table-header-group bg-slate-100"
      data-streamdown="table-header"
      {...props}
    >
      {children}
    </thead>
  );
}

export function Tbody({
  children,
  ...props
}: JSX.IntrinsicElements["tbody"] & ExtraProps) {
  return (
    <tbody
      className="block sm:table-row-group divide-y divide-slate-200 bg-slate-50/50"
      data-streamdown="table-body"
      {...props}
    >
      {children}
    </tbody>
  );
}

function isHeaderRow(node?: Element): boolean {
  return Boolean(
    (node as { children?: { tagName?: string }[] } | undefined)?.children?.some(
      (c) => c.tagName === "th",
    ),
  );
}

export function Tr({
  node,
  children,
  ...props
}: JSX.IntrinsicElements["tr"] & ExtraProps) {
  const sel = useTableSelection();
  const className = "block sm:table-row border-b border-slate-200 py-2 sm:py-0";

  if (!sel || sel.selectableCount === 0) {
    return (
      <tr className={className} data-streamdown="table-row" {...props}>
        {children}
      </tr>
    );
  }

  if (isHeaderRow(node)) {
    // Select-all lives in the SelectionBar above the table now; this is just a
    // spacer cell to keep the checkbox column aligned.
    return (
      <tr className={className} data-streamdown="table-row" {...props}>
        <th className="hidden w-8 px-2 sm:table-cell" aria-hidden="true" />
        {children}
      </tr>
    );
  }

  const identity = rowIdentity(node as never);
  return (
    <tr className={className} data-streamdown="table-row" {...props}>
      <td className="block w-8 px-2 align-middle sm:table-cell">
        {identity ? (
          <SelectionCheckbox
            checked={sel.selectedKeys.has(identity.key)}
            onChange={() => sel.toggle(identity.key)}
            ariaLabel="Sélectionner la ligne"
          />
        ) : null}
      </td>
      {children}
    </tr>
  );
}

export function Th({
  children,
  ...props
}: JSX.IntrinsicElements["th"] & ExtraProps) {
  return (
    <th
      className="truncate max-w-0 px-4 py-2 text-left text-sm font-semibold"
      data-streamdown="table-header-cell"
      {...props}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  ...props
}: JSX.IntrinsicElements["td"] & ExtraProps) {
  return (
    <td
      className={cn(
        "block",
        "sm:table-cell",
        "sm:truncate",
        "sm:max-w-0",
        "px-4",
        "py-1",
        "sm:py-2",
        "text-sm",
        "[&>span]:!inline-flex",
        "[&>span]:!w-full",
        "[&>span]:!min-w-0",
        "[&>span>span]:!inline-flex",
        "[&>span>span]:!w-full",
        "[&>span>span]:!min-w-0",
      )}
      data-streamdown="table-cell"
      {...props}
    >
      {children}
    </td>
  );
}
